/*
 * "Search..." box rules shared by both vehicle pickers (homepage bar: seatfit-home-picker-list.js; drawer:
 * seatfit-vehicle-picker-combobox.js, also its phone sheet), so every list reacts to typing the same way:
 * - case, spaces, dashes and accents do not count: "f150" finds F-150, "crv" CR-V, "rav 4" RAV4, "model3" Model 3;
 * - a row stays when the text matches, best first:
 *   1 the whole label ("ram" -> RAM, "f150" -> F-150), 2 the start of the label ("toy" -> Toyota, "20" -> 2020),
 *   3 the start of a later word ("150" -> F-150, "cherokee" -> Grand Cherokee; letters and digits count as
 *     separate words, so "4" finds RAV4), 4 a nickname of the label (NICKNAMES: "chevy" -> Chevrolet,
 *   "vw" -> Volkswagen), 5 a text that starts with the whole label ("silverado 1500" -> Silverado);
 *   rows of the same rank keep the list's own order (years newest first, makes A-Z), so a text that matched the
 *   start of labels before still lists those rows first, in the same order; "ota" still does not find Toyota;
 * - a Model list given its make (lead) also takes a text that starts with that make or its nickname, when the whole
 *   text finds nothing: "ram 1500" in RAM's models -> 1500, "chevy silverado" in Chevrolet's -> Silverado;
 * - nothing matches -> one grey "No match - check spelling" row that cannot be picked (no longer the whole list);
 * - typing highlights no row; ArrowDown goes to the first row, Enter picks the highlighted row, or the first
 *   match when the shopper typed and pressed Enter straight away.
 * Ranked rows are moved to the top of the list in the DOM, so arrow keys and Enter follow what the shopper sees;
 * an empty text puts every row back in the list's order. Loaded `defer` before the two list scripts.
 */
(function () {
  'use strict';
  if (window.SeatFitPickerSearch) return;

  var NO_MATCH_TEXT = 'No match \u2014 check spelling';
  // Label (as compact() writes it) -> what shoppers type for it. A nickname only finds labels the list holds:
  // "range rover" shows Land Rover in the Make list; in Land Rover's Model list the label start finds Range Rover.
  var NICKNAMES = {
    chevrolet: ['chevy', 'chevvy'], volkswagen: ['vw', 'vdub', 'volkswagon'], mercedesbenz: ['benz', 'mb'],
    landrover: ['rangerover'], bmw: ['bimmer', 'beemer', 'beamer'], cadillac: ['caddy', 'caddie'],
    lamborghini: ['lambo'], porsche: ['porshe', 'porche'], hyundai: ['hyundia', 'hundai'], infiniti: ['infinity'],
    chrysler: ['chrystler'], subaru: ['subie'], mini: ['cooper'], ram: ['dodgeram'], corvette: ['vette'],
    thunderbird: ['tbird']
  };

  // Lower case without accents: "Citroën" -> "citroen".
  function plain(s) {
    return String(s == null ? '' : s).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  }

  // Words of a label or of the typed text, also split where letters meet digits: "F-150" -> f 150, "RAV4" -> rav 4.
  function words(s) {
    return plain(s).replace(/([a-z])(?=[0-9])|([0-9])(?=[a-z])/g, '$1$2 ').split(/[^a-z0-9]+/).filter(Boolean);
  }

  // p: as typed, lower case ("f-150"); c: letters and digits only ("f150"); s: the words, one space ("f 150").
  function key(s) {
    var w = words(s);
    return { p: plain(s).trim(), c: w.join(''), s: w.join(' '), w: w };
  }

  // The typed text, plus brk: it ends in a dash or other mark, i.e. the shopper typed a break there.
  function typedKey(text) {
    var q = key(text);
    q.brk = /[^a-z0-9]$/.test(plain(text).trim());
    return q;
  }

  // 1-5 as listed at the top of the file; 0 = no match. 2.2: the whole label once case, spaces and dashes are
  // dropped, for a text that ends in a dash ("f-150-" lists F-150 before F-150 Raptor). 2.5: label start once they
  // are dropped, after the labels that start with the text exactly as typed ("slc" lists SLC-Class before SL-Class).
  function rank(k, q) {
    if (!q.c || !k.c) return 0;
    if (k.p === q.p || (!q.brk && k.c === q.c)) return 1;
    if (k.p.indexOf(q.p) === 0) return 2;
    if (k.c === q.c) return 2.2;
    // A text ending in a dash wants a break there: "f-" finds F-150 but not Fusion; "ct4-" lists CT4-V before CT4.
    if (q.brk ? k.s !== q.s && (k.s + ' ').indexOf(q.s + ' ') === 0 : k.c.indexOf(q.c) === 0) return 2.5;
    for (var i = 1; i < k.w.length; i++) {
      if (k.w.slice(i).join('').indexOf(q.c) === 0) return 3;
    }
    var nick = NICKNAMES[k.c] || [];
    for (var j = 0; j < nick.length; j++) {
      // "chevy", "chevy silverado", or 3+ letters of a longer nickname ("bim" -> bimmer)
      if (q.c.indexOf(nick[j]) === 0 || (q.c.length >= 3 && nick[j].indexOf(q.c) === 0)) return 4;
    }
    return (q.s + ' ').indexOf(k.s + ' ') === 0 ? 5 : 0; // whole label, then a word break in the text
  }

  // The text after a leading make: lead = the make label; its nickname or, for a two-word make, its first word
  // count too ("benz e class", "mercedes e class" -> "e class"). A typed end dash stays. '' = no make in front.
  function withoutLead(query, lead) {
    var k = key(lead), w = words(query);
    var forms = [k.c].concat(NICKNAMES[k.c] || [], k.w.length > 1 ? [k.w[0]] : []);
    for (var n = w.length - 1; n > 0; n--) {
      if (forms.indexOf(w.slice(0, n).join('')) !== -1) return w.slice(n).join(' ') + (typedKey(query).brk ? '-' : '');
    }
    return '';
  }

  // Positions of the keys the text finds, best rank first, list order within a rank: [{ i, rank }]. When nothing
  // is found and the text starts with the list's lead (the make of a Model list), the rest of the text is ranked.
  function rankAll(keys, query, lead) {
    function ranked(q) {
      var hits = [];
      if (q.c) keys.forEach(function (k, i) { var r = rank(k, q); if (r) hits.push({ i: i, rank: r }); });
      return hits.sort(function (a, b) { return a.rank - b.rank || a.i - b.i; });
    }
    var hits = ranked(typedKey(query));
    var rest = !hits.length && lead ? withoutLead(query, lead) : '';
    return rest ? ranked(typedKey(rest)) : hits;
  }

  // Same rules on plain labels (no DOM), for checks outside the page: [{ i, rank, label }].
  function match(labels, query, lead) {
    return rankAll(labels.map(key), query, lead).map(function (h) {
      return { i: h.i, rank: h.rank, label: labels[h.i] };
    });
  }

  function rowKey(li) {
    if (!li.sfKey) li.sfKey = key(li.textContent); // a row's label never changes; rebuilt lists bring new rows
    return li.sfKey;
  }

  // The rows in the list's own order, whatever order an earlier search left them in the DOM.
  function inListOrder(now) {
    now.forEach(function (li, i) { if (li.sfPos == null) li.sfPos = i; });
    return now.slice().sort(function (a, b) { return a.sfPos - b.sfPos; });
  }

  // Shown rows first (ranked), then the hidden rest; nodes move only when the order differs.
  function arrange(list, now, want) {
    for (var i = 0; i < want.length; i++) {
      if (want[i] !== now[i]) {
        want.forEach(function (li) { list.appendChild(li); });
        return;
      }
    }
  }

  function toggleNoMatch(list, on, cls) {
    var row = list.sfNoMatch;
    if (on && !row) {
      row = list.sfNoMatch = document.createElement('li');
      row.className = cls || '';
      row.setAttribute('role', 'option');
      row.setAttribute('aria-disabled', 'true');
      row.setAttribute('data-sf-no-match', '');
      row.textContent = NO_MATCH_TEXT;
    }
    if (on && row.parentNode !== list) list.insertBefore(row, list.firstChild);
    else if (!on && row && row.parentNode === list) list.removeChild(row);
  }

  // rows: the option <li>s; emptyClass: class of the list's own grey "empty" row, given to the no-match row;
  // lead: the make, for a Model list (see rankAll). Returns { rows: the ones left visible, best first; matched }.
  function filter(rows, query, emptyClass, lead) {
    var now = Array.prototype.slice.call(rows);
    if (!now.length) return { rows: [], matched: false };
    var list = now[0].parentNode;
    var all = inListOrder(now);
    var typed = !!key(query).c;
    var show = typed ? rankAll(all.map(rowKey), query, lead).map(function (h) { return all[h.i]; }) : all;
    all.forEach(function (li) { li.sfShow = !typed; });
    show.forEach(function (li) { li.sfShow = true; });
    all.forEach(function (li) { if (li.hidden === li.sfShow) li.hidden = !li.sfShow; });
    if (list) {
      arrange(list, now, typed ? show.concat(all.filter(function (li) { return !li.sfShow; })) : all);
      toggleNoMatch(list, typed && !show.length, emptyClass);
    }
    return { rows: show, matched: typed && show.length > 0 };
  }

  function visible(rows) {
    return Array.prototype.filter.call(rows, function (li) { return !li.hidden; });
  }

  // Next row for a navigation key among the visible rows, or undefined when the key is not one of them.
  // cur = the highlighted row (null when none): ArrowDown then starts at the first row, ArrowUp at the last.
  function step(vis, cur, key) {
    var jump = { ArrowDown: 1, ArrowUp: -1, PageDown: 10, PageUp: -10 }[key];
    if (!jump) return undefined;
    if (!vis.length) return null;
    var i = vis.indexOf(cur);
    if (i === -1) return jump > 0 ? vis[0] : vis[vis.length - 1];
    return vis[Math.max(0, Math.min(vis.length - 1, i + jump))];
  }

  // Row Enter should pick: the highlighted one, else the first match of the typed text, else none.
  function enterRow(vis, cur, matched) {
    if (cur && !cur.hidden) return cur;
    return matched && vis.length ? vis[0] : null;
  }

  // Keeps a row in view by scrolling only the list (scrollIntoView would also move the drawer / page).
  function reveal(list, li) {
    if (!li) return;
    if (li.offsetTop < list.scrollTop) list.scrollTop = li.offsetTop;
    else if (li.offsetTop + li.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = li.offsetTop + li.offsetHeight - list.clientHeight;
    }
  }

  window.SeatFitPickerSearch = {
    filter: filter, visible: visible, step: step, enterRow: enterRow, reveal: reveal, match: match,
    noMatchText: NO_MATCH_TEXT
  };
})();
